import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/common/Seo";
import { HospitalCard, JourneyCard } from "@/components/common/Cards";
import { SourceBadge } from "@/components/common/Badges";
import { OrganIcon, ORGAN_COLORS, OrganImage } from "@/components/common/OrganIcon";

export default function StatePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setData(null); setErr(false);
    api.get(`/states/${code}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [code]);

  if (err) return <div className="max-w-7xl mx-auto px-6 py-24">State not found.</div>;
  if (!data) return <div className="max-w-7xl mx-auto px-6 py-24 text-neutral-500">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <Seo title={`Transplant centers in ${data.name} — Transplant Story`} description={`${data.name} transplant centers and patient journeys. ${data.centre_count} centers listed.`} />
      <div className="text-[11px] font-mono uppercase tracking-widest text-rust mb-4">{data.type === "ut" ? "Union Territory" : "State"}</div>
      <h1 className="text-4xl sm:text-6xl font-light tracking-tight text-ink">{data.name}</h1>

      <div className="grid grid-cols-2 gap-8 mt-12 max-w-md">
        <Stat value={data.centre_count} label="Transplant centers" />
        <Stat value={data.journey_count} label="Patient journeys" />
      </div>
      <div className="mt-5"><SourceBadge source={data.official_source.source} updated={data.official_source.updated} /></div>

      {data.organs.length > 0 && (
        <>
          <h2 className="text-xl font-medium text-ink mt-16 mb-5">Organ programs available in {data.name}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {data.organs.map((o) => {
              const count = data.centres.filter((c) => (c.organs || []).includes(o.slug)).length;
              const jcount = data.journeys.filter((j) => j.organ === o.slug).length;
              const col = ORGAN_COLORS[o.slug] || ORGAN_COLORS.other;
              return (
                <button
                  key={o.slug}
                  data-testid={`state-organ-${o.slug}`}
                  onClick={() => navigate(`/organ/${o.slug}?state=${data.code}`)}
                  style={{ backgroundColor: col.bg, "--oc-border": col.border }}
                  className="organ-card group p-5 flex flex-col items-start justify-between text-left h-44 overflow-hidden"
                >
                  <span className="transition-transform duration-200 group-hover:scale-105">
                    <OrganImage slug={o.slug} className="w-16 h-16" rounded="rounded-xl" />
                  </span>
                  <div className="w-full">
                    <div className="text-lg font-medium text-ink">{o.name}</div>
                    <div className="text-[11px] font-mono text-neutral-500 mt-1.5 leading-relaxed">
                      <span className="block whitespace-nowrap">{count} {count === 1 ? "center" : "centers"}</span>
                      <span className="block whitespace-nowrap">{jcount} {jcount === 1 ? "journey" : "journeys"}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {data.journeys.length > 0 ? (
        <>
          <h2 className="text-2xl font-normal tracking-tight text-ink mt-20 mb-6">Patient journeys</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.journeys.map((j) => <JourneyCard key={j.id} journey={j} />)}
          </div>
        </>
      ) : (
        <div className="border border-dashed border-neutral-300 p-10 mt-16 text-center">
          <p className="text-neutral-700 font-medium">No patient journeys shared yet.</p>
          <p className="text-sm text-neutral-500 mt-2">This is acceptable and expected. We never hide states with zero stories.</p>
        </div>
      )}

      {data.centre_count > 0 && (
        <>
          <h2 className="text-2xl font-normal tracking-tight text-ink mt-20 mb-6">
            Transplant centers in {data.name} <span className="font-mono text-sm text-neutral-400">({data.centre_count})</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.centres.map((c) => <HospitalCard key={c.id} centre={c} />)}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="text-4xl sm:text-5xl font-light font-mono text-ink tabular-nums">{value}</div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mt-2">{label}</div>
    </div>
  );
}
